import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  TextField,
  Button,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Flight,
  Hotel,
  CalendarMonth,
  FilterAlt,
  Search,
} from '@mui/icons-material';
import {
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import FlightHotelIcon from './FlightHotelIcon';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import { getVendorStatementAPI } from '../operations/transactionService';

/* ================= INTERFACES ================= */

interface VendorStatementRow {
  date: string;
  type: 'FLIGHT' | 'HOTEL';
  sector_city: string;
  passenger_name: string;
  vendor_name: string;
  supplier_reference: string;
  itinerary_number: string;
  supplier_amount: string;
  selling_amount: string;
  refund_amount: string;
}

interface Column {
  id: keyof VendorStatementRow;
  label: string;
  minWidth: number;
  align?: 'left' | 'right' | 'center';
  sx?: object;
}

interface Filters {
  fromDate: string;
  toDate: string;
  type: string;
  supplierName: string;
  supplierReference: string;
}

interface ApiFilters {
  fromDate: string;
  toDate: string;
  type: string;
}

/* ================= CONSTANTS ================= */

const columns: Column[] = [
  { id: 'date',               label: 'DATE',               minWidth: 160, align: 'left', sx: { pl: 3 } },
  { id: 'type',               label: 'TYPE',               minWidth: 70,  align: 'left'  },
  { id: 'vendor_name',        label: 'SUPPLIER NAME',      minWidth: 160, align: 'left'  },
  { id: 'sector_city',        label: 'SECTOR / CITY',      minWidth: 150, align: 'left'  },
  { id: 'passenger_name',     label: 'PASSENGER NAME',     minWidth: 220, align: 'left', sx: { pl: 3 } },
  { id: 'supplier_reference', label: 'SUPPLIER REFERENCE', minWidth: 180, align: 'left'  },
  { id: 'itinerary_number',   label: 'ITINERARY NUMBER',   minWidth: 170, align: 'left'  },
  { id: 'supplier_amount',    label: 'SUPPLIER AMOUNT',    minWidth: 160, align: 'right' },
  { id: 'selling_amount',     label: 'SELLING AMOUNT',     minWidth: 160, align: 'right' },
  { id: 'refund_amount',      label: 'REFUND AMOUNT',      minWidth: 160, align: 'right' },
];

/* ================= HELPERS ================= */

const getDefaultFromDate = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
};

const getDefaultToDate = (): string => new Date().toISOString().split('T')[0];

const formatDate = (date?: string): string => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatAmount = (value: number | string): string => {
  if (value === '' || value === null || value === undefined) return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toFixed(2);
};

const addHyphenAfterThree = (value?: string): string => {
  if (!value) return '-';
  return value.match(/.{1,3}/g)?.join('-') ?? value;
};

const toTitleCase = (value?: string): string => {
  if (!value) return '-';
  return value
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/* ================= COMPONENT ================= */

const VendorStatement = () => {
  const [rows, setRows] = useState<VendorStatementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    fromDate: getDefaultFromDate(),
    toDate: getDefaultToDate(),
    type: 'ALL',
    supplierName: '',
    supplierReference: '',
  });
  const [apiFilters, setApiFilters] = useState<ApiFilters>({
    fromDate: getDefaultFromDate(),
    toDate: getDefaultToDate(),
    type: 'ALL',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  useEffect(() => {
    fetchVendorStatement(apiFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilters]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchVendorStatement(apiFilters);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilters]);

  const fetchVendorStatement = async (currentFilters: ApiFilters) => {
    try {
      setLoading(true);
      const BATCH_LIMIT = 100;

      // Fetch first page to get total_pages
      const firstResponse = await getVendorStatementAPI({
        from_date: currentFilters.fromDate,
        to_date: currentFilters.toDate,
        type: currentFilters.type,
        page: 1,
        limit: BATCH_LIMIT,
      });

      const totalPages: number = firstResponse?.pagination?.total_pages ?? 1;
      let allData: VendorStatementRow[] = Array.isArray(firstResponse?.data)
        ? firstResponse.data
        : [];

      // Fetch remaining pages in parallel
      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const responses = await Promise.all(
          pageNumbers.map((p) =>
            getVendorStatementAPI({
              from_date: currentFilters.fromDate,
              to_date: currentFilters.toDate,
              type: currentFilters.type,
              page: p,
              limit: BATCH_LIMIT,
            })
          )
        );
        responses.forEach((res) => {
          if (Array.isArray(res?.data)) {
            allData = [...allData, ...res.data];
          }
        });
      }

      setRows(allData);
    } catch (error) {
      console.error('[VendorStatement] fetchVendorStatement failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleApplyFilters = () => {
    setPage(0);
    setApiFilters({
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      type: filters.type === 'FLIGHT_HOTEL' ? 'ALL' : filters.type,
    });
  };

  const handleExcelExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const BATCH_LIMIT = 100;

      // Fetch first page to get total
      const firstResponse = await getVendorStatementAPI({
        from_date: apiFilters.fromDate,
        to_date: apiFilters.toDate,
        type: apiFilters.type,
        page: 1,
        limit: BATCH_LIMIT,
      });

      const totalPages: number = firstResponse?.pagination?.total_pages ?? 1;
      let allData: VendorStatementRow[] = Array.isArray(firstResponse?.data)
        ? firstResponse.data
        : [];

      // Fetch remaining pages in parallel
      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const responses = await Promise.all(
          pageNumbers.map((p) =>
            getVendorStatementAPI({
              from_date: apiFilters.fromDate,
              to_date: apiFilters.toDate,
              type: apiFilters.type,
              page: p,
              limit: BATCH_LIMIT,
            })
          )
        );
        responses.forEach((res) => {
          if (Array.isArray(res?.data)) {
            allData = [...allData, ...res.data];
          }
        });
      }

      // Apply same client-side filters as the table
      const data = allData.filter((r) => {
        if (filters.supplierName && r.vendor_name !== filters.supplierName) return false;
        if (
          filters.supplierReference &&
          !(r.supplier_reference || '').toLowerCase().includes(filters.supplierReference.toLowerCase())
        ) return false;
        if (filters.type !== 'ALL' && r.type !== filters.type) return false;
        return true;
      });

      if (data.length === 0) {
        alert('No data available to export.');
        return;
      }

      const columnHeaders = [
        'Date', 'Type', 'Supplier Name', 'Sector / City', 'Passenger Name',
        'Supplier Reference', 'Itinerary Number', 'Supplier Amount', 'Selling Amount', 'Refund Amount',
      ];
      const columnWidths = [180, 110, 240, 200, 240, 240, 220, 180, 180, 180];

      const hdrBase = [
        'background:#272727', 'color:#ffffff', 'font-weight:bold',
        'font-size:11pt', 'vertical-align:middle',
        'padding:14px 16px', 'border:1px solid #d1d5db',
        'font-family:Arial,Helvetica,sans-serif', 'white-space:nowrap',
        'letter-spacing:0.03em',
      ].join(';');
      const hdrAligns = ['left','left','left','left','left','left','left','right','right','right'];
      const hdrStyle = (i: number) => `${hdrBase};text-align:${hdrAligns[i]}`;

      const cellBase = (even: boolean) => [
        `background:${even ? '#ffffff' : '#f8fafa'}`,
        'font-size:10pt', 'vertical-align:middle',
        'padding:11px 14px', 'border:1px solid #e5e7eb',
        'font-family:Arial,Helvetica,sans-serif',
      ].join(';');
      const cellAligns = ['left','left','left','left','left','left','left','right','right','right'];
      const cellStyle = (even: boolean, colIdx: number) => `${cellBase(even)};text-align:${cellAligns[colIdx]}`;

      const colGroup = `<colgroup>${columnWidths.map(w => `<col width="${w}">`).join('')}</colgroup>`;
      const thead = `<thead><tr>${columnHeaders.map((h, i) => `<th style="${hdrStyle(i)}">${h}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${data.map((row, i) => {
        const cells = [
          formatDate(row.date),
          row.type,
          toTitleCase(row.vendor_name),
          row.sector_city || '-',
          row.passenger_name || '-',
          row.supplier_reference || '-',
          row.itinerary_number || '-',
          `$${formatAmount(row.supplier_amount)}`,
          `$${formatAmount(row.selling_amount)}`,
          row.refund_amount && Number(row.refund_amount) !== 0
            ? `$${formatAmount(row.refund_amount)}` : '-',
        ];
        return `<tr>${cells.map((c, ci) => `<td style="${cellStyle(i % 2 === 0, ci)}">${c}</td>`).join('')}</tr>`;
      }).join('')}</tbody>`;

      const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>Vendor Statement</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body><table style="border-collapse:collapse;">${colGroup}${thead}${tbody}</table></body>
</html>`;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.setAttribute('href', url);
      anchor.setAttribute('download', `vendor-statement_${apiFilters.fromDate}_${apiFilters.toDate}.xls`);
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      setTimeout(() => {
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? error?.message ?? String(error);
      console.error('[VendorStatement] handleExcelExport failed:', error);
      alert(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  const handlePdfExport = async () => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const BATCH_LIMIT = 100;

      const firstResponse = await getVendorStatementAPI({
        from_date: apiFilters.fromDate,
        to_date: apiFilters.toDate,
        type: apiFilters.type,
        page: 1,
        limit: BATCH_LIMIT,
      });

      const totalPages: number = firstResponse?.pagination?.total_pages ?? 1;
      let allData: VendorStatementRow[] = Array.isArray(firstResponse?.data)
        ? firstResponse.data
        : [];

      if (totalPages > 1) {
        const pageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const responses = await Promise.all(
          pageNumbers.map((p) =>
            getVendorStatementAPI({
              from_date: apiFilters.fromDate,
              to_date: apiFilters.toDate,
              type: apiFilters.type,
              page: p,
              limit: BATCH_LIMIT,
            })
          )
        );
        responses.forEach((res) => {
          if (Array.isArray(res?.data)) {
            allData = [...allData, ...res.data];
          }
        });
      }

      // Apply same client-side filters as the table
      const data = allData.filter((r) => {
        if (filters.supplierName && r.vendor_name !== filters.supplierName) return false;
        if (
          filters.supplierReference &&
          !(r.supplier_reference || '').toLowerCase().includes(filters.supplierReference.toLowerCase())
        ) return false;
        if (filters.type !== 'ALL' && r.type !== filters.type) return false;
        return true;
      });

      if (data.length === 0) {
        alert('No data available to export.');
        return;
      }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Vendor Statement', 40, 40);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Period: ${apiFilters.fromDate} to ${apiFilters.toDate}`, 40, 58);

      autoTable(doc, {
        startY: 72,
        margin: { left: 40, right: 40 },
        tableWidth: 'auto',
        head: [[
          'Date', 'Type', 'Supplier Name', 'Sector / City',
          'Passenger Name', 'Supplier Reference', 'Itinerary Number',
          'Supplier Amt', 'Selling Amt', 'Refund Amt',
        ]],
        body: data.map((row) => [
          formatDate(row.date),
          row.type,
          toTitleCase(row.vendor_name),
          row.sector_city || '-',
          row.passenger_name || '-',
          row.supplier_reference || '-',
          row.itinerary_number || '-',
          `$${formatAmount(row.supplier_amount)}`,
          `$${formatAmount(row.selling_amount)}`,
          row.refund_amount && Number(row.refund_amount) !== 0
            ? `$${formatAmount(row.refund_amount)}`
            : '-',
        ]),
        styles: {
          fontSize: 7.5,
          cellPadding: 5,
          overflow: 'linebreak',
          font: 'helvetica',
          valign: 'middle',
          halign: 'left',
        },
        headStyles: {
          fillColor: [39, 39, 39],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 7.5,
          halign: 'left',
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        tableLineColor: [229, 231, 235],
        tableLineWidth: 0.4,
        columnStyles: {
          0: { cellWidth: 68, halign: 'left'  },  // Date
          1: { cellWidth: 44, halign: 'left'  },  // Type
          2: { cellWidth: 90, halign: 'left'  },  // Supplier Name
          3: { cellWidth: 70, halign: 'left'  },  // Sector / City
          4: { cellWidth: 90, halign: 'left'  },  // Passenger Name
          5: { cellWidth: 92, halign: 'left'  },  // Supplier Reference
          6: { cellWidth: 82, halign: 'left'  },  // Itinerary Number
          7: { cellWidth: 62, halign: 'right' },  // Supplier Amt
          8: { cellWidth: 62, halign: 'right' },  // Selling Amt
          9: { cellWidth: 62, halign: 'right' },  // Refund Amt
        },
      });

      doc.save(`vendor-statement_${apiFilters.fromDate}_${apiFilters.toDate}.pdf`);
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? error?.message ?? String(error);
      console.error('[VendorStatement] handlePdfExport failed:', error);
      alert(`PDF export failed: ${msg}`);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const itineraryTypeMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    rows.forEach((r) => {
      if (!r.itinerary_number) return;
      if (!map.has(r.itinerary_number)) map.set(r.itinerary_number, new Set());
      map.get(r.itinerary_number)!.add(r.type);
    });
    return map;
  }, [rows]);

  const supplierOptions = useMemo(() => {
    let filtered: VendorStatementRow[];
    if (filters.type === 'FLIGHT_HOTEL') {
      const map = new Map<string, Set<string>>();
      rows.forEach((r) => {
        if (!r.itinerary_number) return;
        if (!map.has(r.itinerary_number)) map.set(r.itinerary_number, new Set());
        map.get(r.itinerary_number)!.add(r.type);
      });
      filtered = rows.filter((r) => {
        const types = map.get(r.itinerary_number);
        return types?.has('FLIGHT') && types?.has('HOTEL');
      });
    } else if (filters.type === 'ALL') {
      filtered = rows;
    } else {
      filtered = rows.filter((r) => r.type === filters.type);
    }
    return Array.from(new Set(filtered.map((r) => r.vendor_name).filter(Boolean))).sort();
  }, [rows, filters.type]);

  const filteredRows = useMemo(() => {
    const result = rows.filter((r) => {
      if (filters.supplierName && r.vendor_name !== filters.supplierName) return false;
      if (
        filters.supplierReference &&
        !(r.supplier_reference || '').toLowerCase().includes(filters.supplierReference.toLowerCase())
      ) return false;
      if (filters.type === 'FLIGHT_HOTEL') {
        const types = itineraryTypeMap.get(r.itinerary_number);
        if (!types?.has('FLIGHT') || !types?.has('HOTEL')) return false;
      } else if (filters.type !== 'ALL' && r.type !== filters.type) {
        return false;
      }
      return true;
    });

    if (filters.type === 'FLIGHT_HOTEL') {
      result.sort((a, b) => {
        if (a.itinerary_number < b.itinerary_number) return -1;
        if (a.itinerary_number > b.itinerary_number) return 1;
        if (a.type === 'FLIGHT' && b.type === 'HOTEL') return -1;
        if (a.type === 'HOTEL' && b.type === 'FLIGHT') return 1;
        return 0;
      });
    }

    return result;
  }, [rows, filters.supplierName, filters.supplierReference, filters.type, itineraryTypeMap]);


  const getTypeIcon = (type: 'FLIGHT' | 'HOTEL', itineraryNumber: string) => {
    const types = itineraryTypeMap.get(itineraryNumber);
    if (types?.has('FLIGHT') && types?.has('HOTEL')) {
      return (
        <FlightHotelIcon
          sx={{
            color: '#4f46e5',
            mr: 0.5,
            fontSize: '28px',
            backgroundColor: '#dbeafe',
            borderRadius: '4px',
            padding: '5px',
          }}
        />
      );
    }
    if (type === 'FLIGHT') {
      return (
        <Flight
          sx={{
            mr: 0.5,
            fontSize: '28px',
            color: '#1976d2',
            backgroundColor: '#dbeafe',
            borderRadius: '4px',
            padding: '5px',
          }}
        />
      );
    }
    return (
      <Hotel
        sx={{
          mr: 0.5,
          fontSize: '28px',
          color: '#7e22ce',
          backgroundColor: '#f3e8ff',
          borderRadius: '4px',
          padding: '5px',
        }}
      />
    );
  };

  return (
    <Box className="transactionList">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        Vendor Statement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage vendor transaction statements
      </Typography>
      </Box>
      <div className="export-group">
      <button type="button" className="btn-export excel" onClick={handleExcelExport} disabled={exporting}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="14" rx="2" stroke="#1A7A1A" stroke-width="1.4"/><path d="M4.5 5L8 8.5L11.5 5" stroke="#1A7A1A" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><line x1="8" y1="3" x2="8" y2="8.5" stroke="#1A7A1A" stroke-width="1.4" stroke-linecap="round"/><line x1="4" y1="12" x2="12" y2="12" stroke="#1A7A1A" stroke-width="1.4" stroke-linecap="round"/></svg>
        {exporting ? 'Exporting...' : 'Excel'}
      </button>
      <button type="button" className="btn-export pdf" onClick={handlePdfExport} disabled={exportingPdf}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="14" height="14" rx="2" stroke="#B33A3A" stroke-width="1.4"/><path d="M4.5 5L8 8.5L11.5 5" stroke="#B33A3A" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><line x1="8" y1="3" x2="8" y2="8.5" stroke="#B33A3A" stroke-width="1.4" stroke-linecap="round"/><line x1="4" y1="12" x2="12" y2="12" stroke="#B33A3A" stroke-width="1.4" stroke-linecap="round"/></svg>
        {exportingPdf ? 'Exporting...' : 'PDF'}
      </button>
    </div>
    </Box>

      {/* Filters */}
      {/* Filters Panel */}
      <Box
        sx={{
          mb: 3,
          px: 2,
            py: 2,
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d1d5db',
          },
          '& .MuiInputLabel-root.Mui-focused': { color: 'text.secondary' },
        }}
      >
        {/* Panel header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid #f1f5f9',
            backgroundColor: '#f8fafc',
          }}
        >
          <FilterAlt sx={{ fontSize: '16px', color: '#1976d2' }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#374151', letterSpacing: '0.02em' }}>
            Filter Results
          </Typography>
        </Box>

        {/* Controls row */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: 2,
            
          }}
        >
          {/* ── Date range group ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1.5,
              backgroundColor: '#bfdbfe1f',
              border: '1px solid #bfdbfe',
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                From Date
              </Typography>
              <TextField
                type="date"
                size="small"
                value={filters.fromDate}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val <= getDefaultToDate()) handleFilterChange('fromDate', val);
                }}
                inputProps={{ max: getDefaultToDate() }}
                sx={{
                  minWidth: 148,
                  backgroundColor: '#fff',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bfdbfe' },
                  '& .MuiInputBase-root': { height: '40px' },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                To Date
              </Typography>
              <TextField
                type="date"
                size="small"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                inputProps={{ max: getDefaultToDate() }}
                sx={{
                  minWidth: 148,
                  backgroundColor: '#fff',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bfdbfe' },
                  '& .MuiInputBase-root': { height: '40px' },
                }}
              />
            </Box>

            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={loading}
              sx={{
                backgroundColor: '#272727',
                '&:hover': { backgroundColor: '#1a1a1a' },
                textTransform: 'none',
                fontWeight: 600,
                height: 40,
                px: 3,
                minWidth: 'auto',
                borderRadius: 1,
              }}
            >
              Filter
            </Button>
          </Box>

          {/* ── Instant filters group ── */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1.5,
              backgroundColor: '#bfdbfe1f',
              border: '1px solid #bfdbfe',
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Supplier Reference
              </Typography>
              <TextField
                size="small"
                placeholder="Search reference..."
                value={filters.supplierReference}
                onChange={(e) => handleFilterChange('supplierReference', e.target.value)}
                sx={{
                  width: '100%',
                  backgroundColor: '#fff',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00000026' },
                  '& .MuiInputBase-root': { height: '40px' },
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 0.5, color: '#9ca3af', fontSize: '18px' }} />,
                }}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Type
              </Typography>
              <FormControl size="small" sx={{ width: '100%', backgroundColor: '#fff', borderRadius: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bfdbfe' }, '& .MuiInputBase-root': { height: '40px' }, }}>
                <Select
                  className="leftFilterBox"
                  value={filters.type}
                  onChange={(e) => {
                    handleFilterChange('type', String(e.target.value));
                    handleFilterChange('supplierName', '');
                  }}
                >
                  <MenuItem value="ALL">All Types</MenuItem>
                  <MenuItem value="FLIGHT">Flight</MenuItem>
                  <MenuItem value="HOTEL">Hotel</MenuItem>
                  <MenuItem value="FLIGHT_HOTEL">Flight + Hotel</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Supplier Name
              </Typography>
              <FormControl size="small" sx={{ width: '100%', backgroundColor: '#fff', borderRadius: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bfdbfe' }, '& .MuiInputBase-root': { height: '40px' }, }}>
                <Select
                  className="leftFilterBox"
                  value={filters.supplierName}
                  onChange={(e) => handleFilterChange('supplierName', String(e.target.value))}
                  displayEmpty
                >
                  <MenuItem value="">All Suppliers</MenuItem>
                  {supplierOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <Paper
        sx={{
          height: '95vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table sx={{ minWidth: 1400 }}>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#f1f3f4',
              }}
            >
              <TableRow
                sx={{
                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      minWidth: col.minWidth,
                      backgroundColor: '#f1f3f4',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      ...col.sx,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Box
                      sx={{
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      No Data Match
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                  const hasDate = Boolean(row.date?.trim());
                  const hasRefund =
                    row.refund_amount !== '' &&
                    row.refund_amount !== null &&
                    row.refund_amount !== undefined &&
                    Number(row.refund_amount) !== 0;

                  return (
                    <TableRow
                      key={`${row.itinerary_number}-${index}`}
                      hover
                      sx={{ cursor: 'pointer' }}
                    >
                      {/* Date */}
                      <TableCell align="left" sx={{ pl: 3 }}>
                        <Box display="flex" alignItems="center" justifyContent="flex-start" gap={0.5}>
                          {hasDate && (
                            <CalendarMonth fontSize="small" sx={{ color: '#059669' }} />
                          )}
                          {hasDate ? formatDate(row.date) : '-'}
                        </Box>
                      </TableCell>

                      {/* Type */}
                      <TableCell align="left">
                        <Box display="flex" alignItems="center" justifyContent="flex-start">
                          {getTypeIcon(row.type, row.itinerary_number)}
                        </Box>
                      </TableCell>

                      {/* Supplier Name */}
                      <TableCell align="left">{toTitleCase(row.vendor_name)}</TableCell>

                      {/* Sector / City */}
                      <TableCell align="left">{row.sector_city || '-'}</TableCell>

                      {/* Passenger Name */}
                      <TableCell align="left" sx={{ pl: 3 }}>{row.passenger_name || '-'}</TableCell>

                      {/* Supplier Reference */}
                      <TableCell align="left">
                        {row.supplier_reference && row.supplier_reference.length > 15 ? (
                          <Tooltip title={row.supplier_reference} placement="top" arrow>
                            <span style={{ cursor: 'default' }}>
                              {row.supplier_reference.slice(0, 15)}…
                            </span>
                          </Tooltip>
                        ) : (
                          row.supplier_reference || '-'
                        )}
                      </TableCell>

                      {/* Itinerary Number */}
                      <TableCell align="left">{addHyphenAfterThree(row.itinerary_number)}</TableCell>

                      {/* Supplier Amount */}
                      <TableCell align="right">${formatAmount(row.supplier_amount)}</TableCell>

                      {/* Selling Amount */}
                      <TableCell align="right">${formatAmount(row.selling_amount)}</TableCell>

                      {/* Refund Amount */}
                      <TableCell align="right">
                        {hasRefund ? (
                          <Chip
                            label={`$${formatAmount(row.refund_amount)}`}
                            size="small"
                            sx={{
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 30, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #e5e7eb' }}
        />
      </Paper>
    </Box>
  );
};

export default VendorStatement;
